/**
 * ThinkingPanel — the collapsible "Thinking…" feed that renders above the
 * assistant's reply while the SSE stream is producing status / node_started /
 * tool_call events. Mirrors Claude Code's UX:
 *
 *   • Streaming, no tokens yet → spinner + latest step, panel expanded
 *   • Streaming, tokens started → auto-collapse to "Thinking (Xs)" chip
 *   • Turn done → collapsed "Thought for Xs" chip; click to review the trail
 *
 * The consumer feeds it the `ThoughtStep[]` accumulated by
 * `useConversationStreamingFacade`.
 */

import React from 'react';
import { ChevronDown, ChevronUp, Loader2, Sparkles } from 'lucide-react';

import type { ThoughtStep } from '@ideoz/core';
import { cn } from '../../lib/utils';

export interface ThinkingPanelProps {
  thoughts: ThoughtStep[];
  /** True while the SSE stream is still open. */
  isStreaming: boolean;
  /** True once the assistant emitted its first visible token this turn. */
  hasStartedResponse: boolean;
  /**
   * Wall-clock ms from first step to first token (or turn end). Falsy while
   * still thinking; used to render "Thought for Xs" once frozen.
   */
  durationMs?: number | null;
  className?: string;
  isRtl?: boolean;
}

/**
 * Fallback formatter for the raw id. Only used when the backend event doesn't
 * carry a `label` — the AI is the source of truth for the human phrasing.
 */
const humaniseId = (raw: string): string => {
  const trimmed = raw.replace(/[_-]+/g, ' ').trim();
  if (!trimmed) return raw;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const stepLabel = (step: ThoughtStep): string => {
  if (step.label && step.label.trim()) return step.label;
  return humaniseId(step.id);
};

/**
 * Fold consecutive identical steps (same kind + label) into a single entry
 * with a repeat count. Backend often fires the same tool_call rapidly (e.g.
 * `ci` ×10) — one line with `×10` reads much cleaner than ten lines.
 */
interface GroupedStep {
  step: ThoughtStep;
  count: number;
}

const groupConsecutive = (steps: ThoughtStep[]): GroupedStep[] => {
  const groups: GroupedStep[] = [];
  for (const step of steps) {
    const last = groups[groups.length - 1];
    if (last && last.step.kind === step.kind && last.step.id === step.id) {
      last.count += 1;
    } else {
      groups.push({ step, count: 1 });
    }
  }
  return groups;
};

const formatSeconds = (ms: number): string => {
  const s = Math.max(1, Math.round(ms / 1000));
  return `${s}s`;
};

export const ThinkingPanel: React.FC<ThinkingPanelProps> = ({
  thoughts,
  isStreaming,
  hasStartedResponse,
  durationMs,
  className,
  isRtl,
}) => {
  // Auto-collapse once the answer starts streaming; the user can still click
  // to re-open the trail.
  const [manuallyToggled, setManuallyToggled] = React.useState<boolean | null>(null);
  const isDone = !isStreaming;
  const hasThoughts = thoughts.length > 0;
  // When the panel is *empty* but the turn is live, we render a minimal
  // "Thinking…" placeholder so there's never a silent gap between "send" and
  // the first SSE event (mirrors Claude Code / Claude app behaviour).
  const isPlaceholderOnly = isStreaming && !hasThoughts;

  // Don't render anything for a finished turn that produced no thoughts —
  // there's nothing to summarise.
  if (isDone && !hasThoughts) return null;

  // Never auto-expand — the panel stays collapsed until the user clicks
  // the header. Same trail is still available on demand.
  const isExpanded = manuallyToggled ?? false;

  const latest = hasThoughts ? thoughts[thoughts.length - 1] : null;
  const summary = isPlaceholderOnly
    ? 'Thinking…'
    : isDone
      ? `Thought for ${formatSeconds(durationMs ?? 0)}`
      : hasStartedResponse
        ? `Thinking (${durationMs ? formatSeconds(durationMs) : '…'})`
        : latest
          ? stepLabel(latest)
          : 'Thinking…';

  return (
    <div
      className={cn('inline-flex flex-col text-sm w-full mb-2', isRtl ? 'mr-0' : 'ml-0', className)}
      data-testid="thinking-panel"
    >
      <button
        type="button"
        onClick={() => hasThoughts && setManuallyToggled(!isExpanded)}
        aria-expanded={isExpanded}
        // No thoughts yet → nothing to expand, disable the toggle affordance.
        disabled={!hasThoughts}
        className={cn(
          'flex items-center gap-2 px-0 py-1.5 text-gray-600 dark:text-gray-300',
          hasThoughts && 'hover:text-gray-900 dark:hover:text-gray-100',
          isPlaceholderOnly && 'animate-pulse'
        )}
      >
        {isDone ? (
          <Sparkles size={12} className="text-gray-400 dark:text-gray-500" />
        ) : (
          <Loader2 size={12} className="animate-spin text-primary-500" />
        )}
        <span className="truncate max-w-[36ch]">{summary}</span>
        {hasThoughts &&
          (isExpanded ? (
            <ChevronUp size={12} className="text-gray-400 dark:text-gray-500" />
          ) : (
            <ChevronDown size={12} className="text-gray-400 dark:text-gray-500" />
          ))}
      </button>

      {isExpanded && hasThoughts && (
        <ol
          className={cn(
            'flex flex-col gap-1 max-h-60 overflow-y-auto',
            'mx-3 px-4 py-3 bg-gray-100 rounded-md text-gray-500 dark:text-gray-400'
          )}
          data-testid="thinking-panel-trail"
        >
          {groupConsecutive(thoughts).map((g, i) => (
            <li key={`${g.step.at}-${i}`} className="flex items-start gap-2">
              <span
                className={cn(
                  'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                  g.step.kind === 'tool'
                    ? 'bg-primary-500'
                    : g.step.kind === 'node'
                      ? 'bg-blue-400'
                      : g.step.kind === 'done'
                        ? 'bg-emerald-500'
                        : 'bg-gray-400 dark:bg-gray-500'
                )}
              />
              <span
                className={cn(
                  'leading-snug',
                  // Emphasise completion summaries (they carry the outcome).
                  g.step.kind === 'done' && 'text-gray-700 dark:text-gray-200'
                )}
              >
                {stepLabel(g.step)}
                {g.count > 1 && (
                  <span className="ml-1.5 text-gray-400 dark:text-gray-500">×{g.count}</span>
                )}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default ThinkingPanel;
